import { useTranslation } from 'next-i18next';
import { FC, useCallback, useMemo } from 'react';
import { useForm, useFormState } from 'react-final-form';
import SkeletonBlock from 'src/common/components/SkeletonLoader/SkeletonBlock';
import { formatDate } from 'src/common/utils/dateHelpers';
import { TripCartFormState } from 'src/listing/types';
import TripCartDropdown, { TripCartDropdownOption } from 'src/trip-cart-deprecated/components/TripCartDropdown';
import { useTripCartContext } from 'src/trip-cart-deprecated/hooks/useTripCartContext';
import { useTripCartOptionsContext } from 'src/trip-cart-deprecated/hooks/useTripCartOptionsContext';

type Values = 'true' | 'false';

const TripCartMultiDayDropdown: FC = () => {
  const [t] = useTranslation('listing-page');
  const { change } = useForm<TripCartFormState>();
  const { values } = useFormState<TripCartFormState>();
  const { formSelectedDates } = values;
  const { instabookAvailabilities, instabookDates } = useTripCartContext();
  const { options } = useTripCartOptionsContext();
  const items = useMemo(() => {
    const [startDate] = formSelectedDates;
    const hasInstabooks = !!instabookAvailabilities.length
    && instabookDates.includes(formatDate(startDate));
    const isSingle = options.isMultiday.length === 1;
    const list: TripCartDropdownOption<Values>[] = [
      {
        id: 'false',
        label: t(isSingle ? 'trip-cart--same-day-only' : 'trip-cart--same-day-toggle'),
        hasInstabook: hasInstabooks,
        icon: 'sun',
      },
      {
        id: 'true',
        label: t(isSingle ? 'trip-cart--multi-day-only' : 'trip-cart--multi-day-toggle'),
        hasInstabook: false,
        icon: 'duration-overnight',
      },
    ];

    if (options.isMultiday.length > 1) {
      return list;
    }

    // remove unused
    if (options.isMultiday[0]) {
      list.shift();
    } else {
      list.pop();
    }
    return list;
  }, [
    formSelectedDates,
    instabookAvailabilities.length,
    instabookDates,
    options.isMultiday,
    t,
  ]);

  const selected = useMemo((): TripCartDropdownOption<Values> => (
    items.find((item) => item.id === values.isMultiDay?.toString())
  ) ?? items[0], [items, values.isMultiDay]);

  const onSelect = useCallback((option: TripCartDropdownOption<Values>) => {
    change('isMultiDay', option.id === 'true');
  }, [change]);

  return (
    <SkeletonBlock loading={!items}>
      <TripCartDropdown
        id="isMultiDay"
        options={items ?? []}
        onSelect={onSelect}
        selectedOption={selected}
      />
    </SkeletonBlock>
  );
};

export default TripCartMultiDayDropdown;
